<script lang="ts" src="./component.ts"></script>
<style lang="scss" src="./component.scss"></style>

<template>
  <SharedCard :class="b()">
    <div :class="b('header')">
      <SharedBack @click="handleBack" />

      <div :class="b('header-content')">
        <h3 :class="b('header-title')">{{ $t("Загрузка документов") }}</h3>
        <p :class="b('header-subtitle')">
          {{
            $t(
              "Фото целиком, без обрезанных краёв и блика. Один документ — один файл"
            )
          }}
        </p>
      </div>
    </div>

    <section :class="b('section')">
      <h5 :class="b('title')">{{ $t("Загрузка паспорта или ВУ*") }}</h5>

      <div :class="b('passport-uploads')">
        <SharedValidate
          v-for="upload in displayedPassportUploads"
          :key="upload.field"
          :field="upload.field"
          :value="documents[upload.field].id"
          :is-validate="isValidate"
          :is-center="true"
        >
          <SharedUpload
            :class="b('upload')"
            :document="documents[upload.field]"
            :is-preview="false"
            :is-loading="isLoading"
            :is-readonly="isReadonly"
            :is-error="isValidate && !documents[upload.field].id"
            @upload="handleUploadFile"
            @remove="handleRemoveFile"
          >
            <template #label>{{ $t(upload.label) }}</template>
          </SharedUpload>
        </SharedValidate>
      </div>
    </section>

    <section :class="b('section')">
      <h5 :class="b('title')">{{ $t("Загрузка селфи*") }}</h5>

      <div :class="b('selfie-actions')">
        <SharedValidate
          field="kyc-personal-documents-selfie"
          :value="documents['kyc-personal-documents-selfie'].id"
          :is-validate="isValidate"
          :is-center="true"
        >
          <SharedUpload
            :class="b('selfie-upload')"
            :document="documents['kyc-personal-documents-selfie']"
            :is-preview="false"
            :is-loading="isLoading"
            :is-readonly="isReadonly"
            :is-center="!documents['kyc-personal-documents-selfie'].id"
            :is-label="!!documents['kyc-personal-documents-selfie'].id"
            :is-error="
              isValidate && !documents['kyc-personal-documents-selfie'].id
            "
            @upload="handleUploadFile"
            @remove="handleRemoveFile"
          >
            <template #label>{{ $t("Селфи с документом") }}</template>
          </SharedUpload>
        </SharedValidate>

        <button
          :class="b('selfie-guide', { active: isSelfieGuideVisible })"
          type="button"
          :aria-expanded="isSelfieGuideVisible"
          @click="handleToggleSelfieGuide"
        >
          <span
            :class="b('selfie-guide-icon')"
            v-html="displayedIcons.camera"
          ></span>
          <span>{{ $t("Какое должно быть селфи") }}</span>
        </button>
      </div>

      <div v-if="isSelfieGuideVisible" :class="b('selfie-panel')">
        <figure v-if="selfieExampleImage" :class="b('selfie-example')">
          <img
            :class="b('selfie-example-image')"
            :src="selfieExampleImage"
            :alt="$t('Пример селфи с документом')"
          />
          <figcaption>{{ $t("Пример") }}</figcaption>
        </figure>

        <ul :class="b('selfie-rules')">
          <li v-for="rule in displayedSelfieRules" :key="rule">
            {{ $t(rule) }}
          </li>
        </ul>
      </div>
    </section>

    <section :class="b('agreements')">
      <h5 :class="b('title')">{{ $t("Скачайте и подпишите соглашения*") }}</h5>
      <p :class="b('section-description')">
        {{
          $t(
            "Скачайте файл, подпишите и загрузите обратно — подпись от руки или ЭЦП."
          )
        }}
      </p>

      <div :class="b('agreements-block')">
        <div
          v-for="agreement in displayedAgreements"
          :key="agreement.field"
          :class="b('agreement-card')"
        >
          <h6 :class="b('agreement-title')">{{ $t(agreement.title) }}</h6>
          <div :class="b('agreement-steps')">
            <div :class="b('agreement-step')">
              <span :class="b('agreement-stage')">{{ $t("1 · Скачать") }}</span>
              <SharedDownload
                :class="b('agreement-download')"
                :document="agreement.document"
              >
                <template #label>{{ $t(agreement.title) }}</template>
              </SharedDownload>
            </div>
            <span
              :class="b('agreement-arrow')"
              v-html="displayedIcons.arrow"
            ></span>
            <div :class="b('agreement-step')">
              <span :class="b('agreement-stage')">{{
                $t("2 · Загрузить")
              }}</span>
              <SharedValidate
                :field="agreement.field"
                :value="documents[agreement.field].id"
                :is-validate="isValidate"
                :is-center="true"
              >
                <SharedUpload
                  :class="b('agreement-upload')"
                  :document="documents[agreement.field]"
                  :is-loading="isLoading"
                  :is-readonly="isReadonly"
                  :is-error="isValidate && !documents[agreement.field].id"
                  @upload="handleUploadFile"
                  @remove="handleRemoveFile"
                >
                  <template #label>{{ $t("Подписанное соглашение") }}</template>
                </SharedUpload>
              </SharedValidate>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="isOtherShow" :class="b('other')">
      <h4 :class="b('other-title')">
        {{
          $t(
            "Пожалуйста, прикрепите иные документы для подтверждения вашей верификации"
          )
        }}
      </h4>
      <p :class="b('other-label')">
        {{
          $t(
            "Например: документ о происхождении денежных средств, документ подтверждающий постановку на учет в налоговом органе и другие"
          )
        }}
      </p>

      <div :class="b('other-documents')">
        <SharedUpload
          v-for="file in files"
          :key="file.uuid"
          :class="b('other-upload')"
          :document="file"
          :is-upload="file.isUpload"
          :is-remove="file.isRemove"
          :is-readonly="isReadonly"
          :is-loading="isLoading"
          @remove="handleRemoveAdditionalFile"
        >
          <template v-if="file.name" #label>{{ file.name }}</template>
        </SharedUpload>

        <SharedUpload
          v-if="isAddtionalUpload"
          :class="b('other-upload')"
          :document="file"
          :is-upload="file.isUpload"
          :is-readonly="isReadonly"
          :is-loading="isLoading"
          :isCenter="true"
          :is-label="false"
          @upload="handleUploadAdditionalFile"
        />
      </div>
    </section>

    <p :class="b('file-info')">
      <b>{{ $t("Размер файла") }}</b> — {{ $t("не более 5 MB.") }}
      <b>{{ $t("Поддерживаемые форматы:") }}</b> PNG, PDF, JPEG, BMP, HEIC,
      DOCX, DOC.
    </p>

    <div :class="b('verification-block')">
      <UIButton
        :class="b('verification')"
        :label="displayedVerificationLabel"
        view="main"
        :disabled="isVerificationDisabled"
        @click="handleVerification"
      />
      <p v-if="isDisabled && !isReadonly" :class="b('verification-hint')">
        {{
          $t(
            "Кнопка станет активной, когда все обязательные блоки будут отмечены галочкой"
          )
        }}
      </p>
    </div>
  </SharedCard>
</template>
